import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowRight, FaShieldAlt, FaRocket, FaHeadset, FaGlobe, FaLock, FaSyncAlt, FaCheck, FaInfoCircle } from 'react-icons/fa';
import SEOHelmet from '../components/SEOHelmet';

const DomainTransfer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [domainToTransfer, setDomainToTransfer] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    contactType: 'PERSON',
    organizationName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    countryCode: 'US',
    zipCode: '',
    phoneNumber: '',
    email: ''
  });

  useEffect(() => {
    // Scroll to top of page when component mounts
    window.scrollTo(0, 0);

    // Check if user is authenticated
    if (!user && !localStorage.getItem('token')) {
      const currentUrl = window.location.pathname;
      const returnUrl = encodeURIComponent(currentUrl);
      navigate(`/login?return=${returnUrl}&message=Please sign in to transfer domains`);
      return;
    }

    // Get domain from navigation state
    if (location.state?.transferDomain) {
      setDomainToTransfer(location.state.transferDomain);
    }

    // Pre-populate contact info from user profile if available
    if (user) {
      setContactInfo(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phone || ''
      }));
    }
  }, [user, location.state, navigate]);

  const handleContactInfoChange = (field: string, value: string) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTransferSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!domainToTransfer) {
      alert('Domain name is required. Please go back to domain search and select a domain to transfer.');
      return;
    }
    
    if (!authCode) {
      alert('Authorization code is required for domain transfer.');
      return;
    }

    // Basic validation for required contact fields
    const requiredFields = ['firstName', 'lastName', 'addressLine1', 'city', 'zipCode', 'phoneNumber', 'email'];
    const missingFields = requiredFields.filter(field => !contactInfo[field as keyof typeof contactInfo]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Prepare transfer data
    const transferData = {
      domainName: domainToTransfer,
      authCode,
      contactInfo,
      transferPrice: 12.99 // Standard transfer price
    };
    
    console.log('Domain transfer data prepared:', transferData);
    
    // Navigate to hosting selection page
    navigate('/domain-transfer-hosting', { state: { transferData } });
  };

  const transferSteps = [
    {
      step: '1',
      title: 'Unlock Your Domain',
      description: 'Contact your current registrar to unlock your domain and obtain the authorization code (EPP code)'
    },
    {
      step: '2', 
      title: 'Enter Domain Details',
      description: 'Enter your domain name and authorization code in the form below'
    },
    {
      step: '3',
      title: 'Confirm Transfer',
      description: 'Review your transfer request and complete the payment process'
    },
    {
      step: '4',
      title: 'Transfer Complete',
      description: 'Your domain will be transferred within 5-7 days and extended by 1 year'
    }
  ];

  const benefits = [
    {
      icon: FaLock,
      title: 'Free WHOIS Privacy',
      description: 'Protect your personal information with complimentary privacy protection'
    },
    {
      icon: FaShieldAlt,
      title: 'Advanced DNS Management',
      description: 'Full DNS control with easy-to-use management tools and instant updates'
    },
    {
      icon: FaSyncAlt,
      title: 'Free 1-Year Extension',
      description: 'Your domain gets extended by 1 year when you transfer to us'
    },
    {
      icon: FaRocket,
      title: 'Zero Downtime',
      description: 'Your website stays online throughout the entire transfer process'
    },
    {
      icon: FaGlobe,
      title: 'Better Pricing',
      description: 'Enjoy competitive renewal rates and transparent pricing with no hidden fees'
    },
    {
      icon: FaHeadset,
      title: '24/7 Expert Support',
      description: 'Get help from our domain experts anytime during the transfer process'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Domain Transfer Service",
    "description": "Transfer your domain to 12and22 and save up to 60%",
    "brand": {
      "@type": "Brand",
      "name": "12and22"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": "12.99"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <SEOHelmet
        title="Domain Transfer - Move Your Domain to 12and22 | Save Up to 60%"
        description="Transfer your domain to 12and22 and save up to 60%. Free WHOIS privacy, no downtime, easy DNS management, and one year extension included with every transfer."
        keywords="domain transfer, transfer domain, move domain, domain migration, domain registrar transfer, bulk domain transfer, EPP code, authorization code, 12and22"
        canonicalUrl="https://12and22.com/domain-transfer"
        structuredData={structuredData}
      />


      {/* Domain & Form Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Domain Being Transferred */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden mb-8">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
                {/* Icon glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <FaGlobe className="w-8 h-8 text-white relative z-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Transferring Domain</h2>
              <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 mb-4">
                {domainToTransfer || 'No domain selected'}
              </div>
              {!domainToTransfer && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-red-700 text-sm">
                    <FaInfoCircle className="inline w-4 h-4 mr-2" />
                    Please select a domain from the domain search page first.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Transfer Form */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-8 py-6 border-b border-orange-200">
                <h3 className="text-xl font-bold text-gray-900">Complete Your Transfer</h3>
                <p className="text-gray-600 mt-1">Provide the required information to initiate your domain transfer</p>
              </div>
            
            <form onSubmit={handleTransferSubmit} className="p-8">
              {/* Authorization Code */}
              <div className="mb-8">
                <label htmlFor="authCode" className="block text-lg font-semibold text-gray-900 mb-3">
                  Authorization Code (EPP Code)
                </label>
                <input
                  type="text"
                  id="authCode"
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value)}
                  placeholder="Enter your authorization code from current registrar"
                  className="w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                  required
                />
                <div className="mt-3 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <FaInfoCircle className="w-4 h-4 mr-2 text-orange-500" />
                    How to get your authorization code
                  </h4>
                  <p className="text-sm text-gray-700">
                    Contact your current registrar and request to unlock your domain and provide the EPP/auth code. 
                    This is usually found in your registrar's control panel under domain settings.
                  </p>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="border-t border-gray-200 pt-8">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h4>
                  <p className="text-gray-600">Required for domain transfer verification</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.firstName}
                      onChange={(e) => handleContactInfoChange('firstName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.lastName}
                      onChange={(e) => handleContactInfoChange('lastName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => handleContactInfoChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={contactInfo.phoneNumber}
                      onChange={(e) => handleContactInfoChange('phoneNumber', e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Contact Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Contact Type *
                    </label>
                    <select
                      value={contactInfo.contactType}
                      onChange={(e) => handleContactInfoChange('contactType', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                    >
                      <option value="PERSON">Individual</option>
                      <option value="COMPANY">Company</option>
                      <option value="ASSOCIATION">Association</option>
                      <option value="PUBLIC_BODY">Public Body</option>
                    </select>
                  </div>

                  {/* Organization Name */}
                  {contactInfo.contactType !== 'PERSON' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        value={contactInfo.organizationName}
                        onChange={(e) => handleContactInfoChange('organizationName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                        required
                      />
                    </div>
                  )}

                  {/* Address Line 1 */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address Line 1 *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.addressLine1}
                      onChange={(e) => handleContactInfoChange('addressLine1', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address Line 2 (Optional)
                    </label>
                    <input
                      type="text"
                      value={contactInfo.addressLine2}
                      onChange={(e) => handleContactInfoChange('addressLine2', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.city}
                      onChange={(e) => handleContactInfoChange('city', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State/Province *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.state}
                      onChange={(e) => handleContactInfoChange('state', e.target.value)}
                      placeholder="CA, NY, etc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country *
                    </label>
                    <select
                      value={contactInfo.countryCode}
                      onChange={(e) => handleContactInfoChange('countryCode', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="IN">India</option>
                      <option value="JP">Japan</option>
                      {/* Add more countries as needed */}
                    </select>
                  </div>

                  {/* Zip Code */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ZIP/Postal Code *
                    </label>
                    <input
                      type="text"
                      value={contactInfo.zipCode}
                      onChange={(e) => handleContactInfoChange('zipCode', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="border-t border-gray-200 pt-8 mt-8">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                  <span className="relative z-10 flex items-center justify-center">
                    <FaSyncAlt className="mr-3" />
                    Initiate Domain Transfer
                    <FaArrowRight className="ml-3" />
                  </span>
                </button>
                
                <p className="text-center text-sm text-gray-600 mt-4">
                  By clicking "Initiate Domain Transfer", you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </form>
            </div>
          </div>
        </div>
      </section>

      {/* Transfer Steps Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">How It Works</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Simple Transfer Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transfer your domain to 12and22 in 4 easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {transferSteps.map((step, index) => (
              <div key={index} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg relative overflow-hidden group hover:shadow-xl transition-all duration-200">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl group-hover:from-white/30 transition-all duration-200"></div>
                
                <div className="relative z-10 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md relative overflow-hidden mx-auto mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                    <span className="font-bold text-white text-lg relative z-10">{step.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden inline-block">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">✨ What You Get with Every Transfer</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">Free transfer</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">+1 year extension</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">WHOIS privacy</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">DNS management</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">24/7 support</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-gray-700">Zero downtime</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Why Transfer to 12and22?</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              More Than Just a Transfer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When you transfer your domain to 12and22, you get access to premium features and better service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon className="text-white text-xl" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about domain transfers
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">How long does a domain transfer take?</h3>
              <p className="text-gray-600">Domain transfers typically take 5-7 days to complete. During this time, your website and email will continue to work normally.</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Will my website go down during the transfer?</h3>
              <p className="text-gray-600">No! Your website, email, and all services will continue to work normally throughout the transfer process. There is zero downtime.</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">What is an authorization code?</h3>
              <p className="text-gray-600">An authorization code (also called EPP code or transfer key) is a unique code that verifies you own the domain. You can get this from your current registrar.</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Are there any fees for transferring?</h3>
              <p className="text-gray-600">We don't charge any transfer fees! You only pay for a 1-year extension at our regular domain pricing, which extends your domain's expiration date.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DomainTransfer;